import { QGL_Lib } from "./QGL_Lib";
import { Ebr_Ejendomsbeliggenhed } from "./DFQGLTypes";
import { parse as WKTparse } from 'wkt';
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";

export class EBRQGL_Lib extends QGL_Lib {


  async EjendomsbeliggenhedForBFEBatch(bfeNrs: string[]): Promise<Array<{ EBR_Ejendomsbeliggenhed: { nodes: Ebr_Ejendomsbeliggenhed[], pageInfo?: { hasNextPage: boolean, endCursor?: string } } }>> {
    if (bfeNrs.length === 0) return [];

    const results: Array<{ EBR_Ejendomsbeliggenhed: { nodes: Ebr_Ejendomsbeliggenhed[], pageInfo?: { hasNextPage: boolean, endCursor?: string } } }> = [];
    const isoNow = new Date().toISOString();

    // Process in chunks of 1000 BFE numbers
    const chunkSize = 1000;
    for (let i = 0; i < bfeNrs.length; i += chunkSize) {
      const chunk = bfeNrs.slice(i, i + chunkSize);
      const bfeList = chunk.map(bfe => `"${bfe}"`).join(",");

      // Fetch all pages for this chunk
      let hasNextPage = true;
      let afterCursor: string | undefined = undefined;

      while (hasNextPage) {
        const afterParam = afterCursor ? `, after: "${afterCursor}"` : "";
        const query = `
query {
  EBR_Ejendomsbeliggenhed(
    virkningstid: "${isoNow}"
    registreringstid: "${isoNow}"
    where: { bestemtFastEjendomBFENr: {in: [${bfeList}]}, status: {eq: "gældende" }}
    first: 1000${afterParam}
  ) {
    pageInfo {
      hasNextPage
      endCursor
    }
    nodes {
      id_lokalId
      id_namespace
      bestemtFastEjendomBFENr
      adresseManueltAngivet
      ESDHReferenceAdresse
      ESDHReferenceKommune
      kommuneManueltAngivet
      kommuneinddelingKommunekode
      betegnelse
      status
      forretningshaendelse
      forretningsomraade
      forretningsproces
      virkningFra
      virkningTil
      virkningsaktoer
      registreringFra
      registreringTil
      registreringsaktoer
      ejendomsbeliggenhedHarSomBeliggenhedsadresseHusnummer {
        datafordelerOpdateringstid
        adgangsadressebetegnelse
        adgangTilBygning
        forretningshaendelse
        forretningsomraade
        forretningsproces
        geoDanmarkBygning
        husnummerretning {
          wkt
        }
        husnummertekst
        id_lokalId
        id_namespace
        jordstykke
        registreringFra
        registreringsaktoer
        status
        vejmidte
        virkningFra
        virkningsaktoer
        husnummerHoererTilIPostnummer {
          datafordelerOpdateringstid
          forretningshaendelse
          forretningsomraade
          forretningsproces
          id_lokalId
          id_namespace
          navn
          postnr
          postnummerinddeling
          registreringFra
          registreringsaktoer
          status
          virkningFra
          virkningsaktoer
        }
        husnummerHoererTilNavngivenVej {
          datafordelerOpdateringstid
          administreresAfKommune
          forretningshaendelse
          forretningsomraade
          forretningsproces
          id_lokalId
          id_namespace
          registreringFra
          registreringsaktoer
          status
          udtaltVejnavn
          vejadresseringsnavn
          vejnavn
          vejnavnebeliggenhed_oprindelse_kilde
          vejnavnebeliggenhed_oprindelse_noejagtighedsklasse
          vejnavnebeliggenhed_oprindelse_registrering
          vejnavnebeliggenhed_oprindelse_tekniskStandard
          vejnavnebeliggenhed_vejnavnelinje {
            wkt
          }
          virkningFra
          virkningsaktoer
        }
        HusnummerHarAdgangspunkt {
          datafordelerOpdateringstid
          oprindelse_kilde
          oprindelse_noejagtighedsklasse
          oprindelse_registrering
          oprindelse_tekniskStandard
          position {
            wkt
          }
        }
        HusnummerHarVejpunkt {
          datafordelerOpdateringstid
          oprindelse_kilde
          oprindelse_noejagtighedsklasse
          oprindelse_registrering
          oprindelse_tekniskStandard
          position {
            wkt
          }
        }
        husnummerLiggerIAfstemningsomraade {
          afstemningsomraadenummer
          navn
        }
        husnummerLiggerIKommuneinddeling {
          kommunekode
          navn
        }
        husnummerLiggerISogneInddeling {
          sognekode
          navn
        }
        husnummerHoererTilSupplerendeBynavn {
          datafordelerOpdateringstid
          forretningshaendelse
          forretningsomraade
          forretningsproces
          id_lokalId
          id_namespace
          navn
          registreringFra
          registreringsaktoer
          status
          supplerendeBynavn
          virkningFra
          virkningsaktoer
        }
      }
    }
  }
}
        `;

        const result = await this.query<{ EBR_Ejendomsbeliggenhed: { nodes: any[], pageInfo?: { hasNextPage: boolean, endCursor?: string } } }>(query);
        results.push(result);

        // Check if there are more pages - if pageInfo doesn't exist, assume no more pages
        const pageInfo = result.EBR_Ejendomsbeliggenhed?.pageInfo;
        hasNextPage = pageInfo?.hasNextPage || false;
        afterCursor = pageInfo?.endCursor;

        // If we got fewer nodes than requested (and less than 1000), we've reached the end
        const nodeCount = result.EBR_Ejendomsbeliggenhed?.nodes?.length || 0;
        if (!hasNextPage && nodeCount < 1000) {
          hasNextPage = false;
        }
      }
    }

    return results;
  }

  async EjendomsbeliggenhedForBFE(bfeNr: string): Promise<{ EBR_Ejendomsbeliggenhed: { nodes: Ebr_Ejendomsbeliggenhed[] } }> {
    let isoNow = new Date().toISOString();
    return this.query<{ EBR_Ejendomsbeliggenhed: { nodes: any[] } }>(`
query {
  EBR_Ejendomsbeliggenhed(
    virkningstid: "${isoNow}"
    registreringstid: "${isoNow}"
    where: { bestemtFastEjendomBFENr: {eq: "${bfeNr}"}, status: {eq: "gældende" }}
  ) {
    nodes {
    
      id_lokalId
      id_namespace
      bestemtFastEjendomBFENr
      adresseManueltAngivet
      ESDHReferenceAdresse
      ESDHReferenceKommune
      kommuneManueltAngivet
      kommuneinddelingKommunekode
      betegnelse
      status
      forretningshaendelse
      forretningsomraade
      forretningsproces
      virkningFra
      virkningTil
      virkningsaktoer
      registreringFra
      registreringTil
      registreringsaktoer
      ejendomsbeliggenhedHarSomBeliggenhedsadresseHusnummer {
        datafordelerOpdateringstid
        adgangsadressebetegnelse
        adgangTilBygning
        forretningshaendelse
        forretningsomraade
        forretningsproces
        geoDanmarkBygning
        husnummerretning {
          wkt
        }
        husnummertekst
        id_lokalId
        id_namespace
        jordstykke
        registreringFra
        registreringsaktoer
        status
        vejmidte
        virkningFra
        virkningsaktoer
        husnummerHoererTilIPostnummer {
          datafordelerOpdateringstid
          forretningshaendelse
          forretningsomraade
          forretningsproces
          id_lokalId
          id_namespace
          navn
          postnr
          postnummerinddeling
          registreringFra
          registreringsaktoer
          status
          virkningFra
          virkningsaktoer
        }
        husnummerHoererTilNavngivenVej {
          datafordelerOpdateringstid
          administreresAfKommune
          forretningshaendelse
          forretningsomraade
          forretningsproces
          id_lokalId
          id_namespace
          registreringFra
          registreringsaktoer
          status
          udtaltVejnavn
          vejadresseringsnavn
          vejnavn
          vejnavnebeliggenhed_oprindelse_kilde
          vejnavnebeliggenhed_oprindelse_noejagtighedsklasse
          vejnavnebeliggenhed_oprindelse_registrering
          vejnavnebeliggenhed_oprindelse_tekniskStandard
          vejnavnebeliggenhed_vejnavnelinje {
            wkt
          }
          virkningFra
          virkningsaktoer
        }
        HusnummerHarAdgangspunkt {
          datafordelerOpdateringstid
          oprindelse_kilde
          oprindelse_noejagtighedsklasse
          oprindelse_registrering
          oprindelse_tekniskStandard
          position {
            wkt
          }
        }
        HusnummerHarVejpunkt {
          datafordelerOpdateringstid
          oprindelse_kilde
          oprindelse_noejagtighedsklasse
          oprindelse_registrering
          oprindelse_tekniskStandard
          position {
            wkt
          }
        }
        husnummerLiggerIAfstemningsomraade {
          afstemningsomraadenummer
          navn
        }
        husnummerLiggerIKommuneinddeling {
          kommunekode
          navn
        }
        husnummerLiggerISogneInddeling {
          sognekode
          navn
        }
        husnummerHoererTilSupplerendeBynavn {
          datafordelerOpdateringstid
          forretningshaendelse
          forretningsomraade
          forretningsproces
          id_lokalId
          id_namespace
          navn
          registreringFra
          registreringsaktoer
          status
          supplerendeBynavn
          virkningFra
          virkningsaktoer
        }
      }
    }
  }
}
      `);
  }

  async EBRAdresseOgPunkt(BFEnr: string[] | string | number[] | number): Promise<EBRAresseOpslag[]> {
    if (typeof BFEnr === "string" || typeof BFEnr === "number") {
      BFEnr = [BFEnr] as string[] | number[];
    }
    let results = await this.EBR_Ejendomsbeliggenhed(BFEnr);
    return results.EBR_Ejendomsbeliggenhed.nodes.map(EBRNodeToAdresseOpslag);
  }

  async EBR_Ejendomsbeliggenhed(bfeNr: string[] | number[]): Promise<{ EBR_Ejendomsbeliggenhed: { nodes: Ebr_Ejendomsbeliggenhed[] } }> {
    let isoNow = new Date().toISOString();
    let qResult = this.query<{ EBR_Ejendomsbeliggenhed: { nodes: any[] } }>(`
query {
  EBR_Ejendomsbeliggenhed(
    virkningstid: "${isoNow}"
    registreringstid: "${isoNow}"
    where: { bestemtFastEjendomBFENr: {in: ${JSON.stringify(bfeNr.map(String))}}, status: {eq: "gældende" }}
  ) {nodes {
    bestemtFastEjendomBFENr
    betegnelse
    status
    ejendomsbeliggenhedHarSomBeliggenhedsadresseAdresse {
      adressebetegnelse
      doerbetegnelse
      bygning
      AdresseHarDoerpunkt {position {wkt}}
      adresseHarHusnummer {
        vejpunkt
        HusnummerHarAdgangspunkt {
          position { wkt }
        }
        adgangspunkt
      	adgangsadressebetegnelse
      }
    }
    ejendomsbeliggenhedHarSomBeliggenhedsadresseHusnummer {
      adgangsadressebetegnelse
      HusnummerHarAdgangspunkt {
        position { wkt }
      }
    }
    ejendomsbeliggenhedKnytterAdresseTilBestemtFastEjendomEJL { nodes {
      StedbestemmelsesGeometri
      ejerlejlighedsnummer
      ejerlejlighedsLodErEnDelAfEjerlejlighed {nodes {
        geometri {wkt}
      }}
    }}
    ejendomsbeliggenhedKnytterAdresseTilBestemtFastEjendomSFE { nodes {
    	StedbestemmelsesReference
    }}
  }} 
}`
    );

    return qResult;


  }
}


export type EBRAresseOpslag = {
  betegnelse: string;
  bfeNr: number;
  geometri?: string;
  lat?: number;
  lng?: number;
}
function EBRNodeToAdresseOpslag(EBR_ejendomsbeliggenhed: Ebr_Ejendomsbeliggenhed): any {
  function koordsFromWKT(wkt?: string): { lat: number, lng: number } | undefined {
    if (!wkt) return undefined;
    let [x, y] = WKTparse(wkt).coordinates;
    let { lat, lng } = GenerateGeomUtils.convertFromUTMtoLatLng({ x, y });
    return { lat, lng };
  }

  if (EBR_ejendomsbeliggenhed.ejendomsbeliggenhedHarSomBeliggenhedsadresseAdresse?.adresseHarHusnummer) {
    let opsl = EBR_ejendomsbeliggenhed.ejendomsbeliggenhedHarSomBeliggenhedsadresseAdresse?.adresseHarHusnummer
    return {
      betegnelse: opsl.adgangsadressebetegnelse,
      bfeNr: parseInt(EBR_ejendomsbeliggenhed.bestemtFastEjendomBFENr),
      geometri: opsl.HusnummerHarAdgangspunkt?.position?.wkt,
      ...koordsFromWKT(opsl.HusnummerHarAdgangspunkt?.position?.wkt)
    } as EBRAresseOpslag
  }
  if (EBR_ejendomsbeliggenhed.ejendomsbeliggenhedHarSomBeliggenhedsadresseHusnummer?.adgangsadressebetegnelse) {
    let opsl = EBR_ejendomsbeliggenhed.ejendomsbeliggenhedHarSomBeliggenhedsadresseHusnummer
    return {
      betegnelse: opsl.adgangsadressebetegnelse,
      bfeNr: parseInt(EBR_ejendomsbeliggenhed.bestemtFastEjendomBFENr),
      geometri: opsl.HusnummerHarAdgangspunkt?.position?.wkt,
      ...koordsFromWKT(opsl.HusnummerHarAdgangspunkt?.position?.wkt)
    } as EBRAresseOpslag
  }
  return {
    betegnelse: EBR_ejendomsbeliggenhed.betegnelse ?? "Ukendt adresse",
    bfeNr: EBR_ejendomsbeliggenhed.bestemtFastEjendomBFENr ?? "Ukendt BFE",
  }
}

